import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useDashboardEvents } from '../contexts/EventContext';
import { useAuth } from '../contexts/AuthContext';
import { motion } from 'framer-motion';
import toast from 'react-hot-toast';
import SafeIcon from '../common/SafeIcon';
import * as FiIcons from 'react-icons/fi';
import { duplicateEvent } from '../services/eventsService';
import { 
  STATUS_FILTER_TABS, 
  filterEventsByStatus, 
  getStatusLabel, 
  getStatusBadgeClasses 
} from '../utils/eventStatusMapping';

const { FiPlus, FiCalendar, FiUsers, FiDollarSign, FiEye, FiEdit, FiTrash2, FiExternalLink, FiCopy } = FiIcons;

const Events = () => {
  const { events, deleteEvent, fetchDashboardData } = useDashboardEvents();
  const { user, role } = useAuth();
  const navigate = useNavigate();
  const [filter, setFilter] = useState('all');
  const [deleting, setDeleting] = useState(null);
  const [duplicating, setDuplicating] = useState(null);

  const userEvents = events.filter(event =>
    role === 'admin' || event.agentId === user?.id
  );

  // Use the centralized filter function that maps filter keys to DB values
  const filteredEvents = filterEventsByStatus(userEvents, filter);

  const handleDelete = async (eventId) => {
    if (!window.confirm('Are you sure you want to delete this event?')) {
      return;
    }

    setDeleting(eventId);
    try {
      await deleteEvent(eventId);
      toast.success('Event deleted successfully');
    } catch (error) {
      console.error('[Events] Delete failed:', error);
      toast.error(`Failed to delete event: ${error.message}`);
    } finally {
      setDeleting(null);
    }
  };

  const handleDuplicate = async (eventId) => {
    setDuplicating(eventId);
    try {
      const { error } = await duplicateEvent(eventId, { role, userId: user?.id });
      if (error) {
        throw error;
      }

      await fetchDashboardData(user?.id, role);
      toast.success('Event duplicated successfully');
    } catch (error) {
      console.error('[Events] Duplicate failed:', error);
      toast.error(`Failed to duplicate event: ${error.message}`);
    } finally {
      setDuplicating(null);
    }
  };

  return (
    <div className="space-y-6">
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Events</h1>
          <p className="text-gray-600 mt-1">Manage your events and track performance</p>
        </div>
        <Link
          to="/dashboard/events/create"
          className="bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700 transition-colors flex items-center space-x-2"
        >
          <SafeIcon icon={FiPlus} className="w-4 h-4" />
          <span>Create Event</span>
        </Link>
      </div>

      <div className="bg-white rounded-lg shadow">
        <div className="p-6 border-b border-gray-200">
          <div className="flex space-x-4">
            {STATUS_FILTER_TABS.map((tab) => (
              <button
                key={tab.key}
                onClick={() => setFilter(tab.key)}
                className={`px-4 py-2 rounded-lg font-medium transition-colors ${
                  filter === tab.key
                    ? 'bg-blue-100 text-blue-700'
                    : 'text-gray-600 hover:text-gray-900'
                }`}
              >
                {tab.label}
              </button>
            ))}
          </div>
        </div>

        <div className="overflow-x-auto">
          <table className="w-full">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Event
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Date & Time
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Registrations
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Price
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Status
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Actions
                </th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {filteredEvents.map((event, index) => (
                <motion.tr
                  key={event.id}
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: index * 0.1 }}
                  className="hover:bg-gray-50"
                >
                  <td className="px-6 py-4">
                    <div>
                      <div className="flex items-center gap-2 flex-wrap">
                        <Link to={`/dashboard/events/${event.id}`} className="font-medium text-gray-900 hover:text-blue-600 transition-colors">
                          {event.title}
                        </Link>
                        {event.usePlatformAccount && (
                          <span className="px-2 py-0.5 rounded text-xs font-medium bg-slate-100 text-slate-700 border border-slate-200">
                            Platform Payment
                          </span>
                        )}
                      </div>
                      <p className="text-sm text-gray-500 truncate max-w-xs">
                        {event.description}
                      </p>
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="flex items-center space-x-2">
                      <SafeIcon icon={FiCalendar} className="w-4 h-4 text-gray-400" />
                      <div>
                        <p className="text-sm text-gray-900">{event.date}</p>
                        <p className="text-xs text-gray-500">{event.time}</p>
                      </div>
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="flex items-center space-x-2">
                      <SafeIcon icon={FiUsers} className="w-4 h-4 text-gray-400" />
                      <span className="text-sm text-gray-900">
                        {event.registrations}/{event.maxAttendees || '∞'}
                      </span>
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="flex items-center space-x-2">
                      <SafeIcon icon={FiDollarSign} className="w-4 h-4 text-gray-400" />
                      <span className="text-sm text-gray-900">
                        {event.price === 0 ? 'Free' : `$${event.price}`}
                      </span>
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <span className={`px-2 py-1 rounded-full text-xs font-medium ${getStatusBadgeClasses(event.status)}`}>
                      {getStatusLabel(event.status)}
                    </span>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="flex items-center space-x-2">
                      <Link
                        to={`/dashboard/events/${event.id}`}
                        className="text-gray-400 hover:text-blue-600 transition-colors"
                      >
                        <SafeIcon icon={FiEye} className="w-4 h-4" />
                      </Link>
                      <Link
                        to={`/landing/${event.id}`}
                        className="text-gray-400 hover:text-green-600 transition-colors"
                      >
                        <SafeIcon icon={FiExternalLink} className="w-4 h-4" />
                      </Link>
                      <button
                        onClick={() => navigate(`/dashboard/events/${event.id}/edit`)}
                        className="text-gray-400 hover:text-blue-600 transition-colors"
                        title="Edit event"
                      >
                        <SafeIcon icon={FiEdit} className="w-4 h-4" />
                      </button>
                      <button
                        onClick={() => handleDuplicate(event.id)}
                        disabled={duplicating === event.id}
                        className={`transition-colors ${
                          duplicating === event.id
                            ? 'text-gray-300 cursor-not-allowed'
                            : 'text-gray-400 hover:text-indigo-600'
                        }`}
                        title="Duplicate event"
                      >
                        <SafeIcon icon={FiCopy} className={`w-4 h-4 ${duplicating === event.id ? 'animate-pulse' : ''}`} />
                      </button>
                      <button
                        onClick={() => handleDelete(event.id)}
                        disabled={deleting === event.id}
                        className={`transition-colors ${
                          deleting === event.id
                            ? 'text-gray-300 cursor-not-allowed'
                            : 'text-gray-400 hover:text-red-600'
                        }`}
                      >
                        <SafeIcon icon={FiTrash2} className={`w-4 h-4 ${deleting === event.id ? 'animate-pulse' : ''}`} />
                      </button>
                    </div>
                  </td>
                </motion.tr>
              ))}
            </tbody>
          </table>
        </div>

        {filteredEvents.length === 0 && (
          <div className="p-12 text-center">
            <SafeIcon icon={FiCalendar} className="w-12 h-12 text-gray-300 mx-auto mb-4" />
            <p className="text-gray-500 text-lg mb-2">No events found</p>
            <p className="text-gray-400">Create your first event to get started</p>
            <Link
              to="/dashboard/events/create"
              className="inline-block mt-4 bg-blue-600 text-white px-6 py-3 rounded-lg hover:bg-blue-700 transition-colors"
            >
              Create Event
            </Link>
          </div>
        )}
      </div>
    </div>
  );
};

export default Events;
